import { useMutation, UseMutationResult } from 'react-query';

import {
  changePassword,
  createStoryService,
  getHashtagList,
  getPublicStoriesService,
  getStoriesService,
  getStoryAnalysis,
  getStoryHashtags,
  getStoryRequestInfo,
  getRecommendedStories,
  getStoryTitles,
  getStoryVideoDownload,
  getStoryVoice,
  getSuggestedStoryService,
  getUserStoriesService,
  getUserStoryDetailsService,
  rateStory,
  saveStory,
  unSaveStory,
  updateStoryVisibility,
  updateStoryContent,
  updateStoryName,
  getStoryInSights,
  getWhatIfStoryVoice,
} from './service';

import { useNavigate } from 'react-router-dom';
import type { MutationError } from '../../type';
import type {
  IChangePassword,
  ICreateStory,
  IGetStoryVoice,
  IRateData,
  IStoryData,
  IStoryAnalysis,
  IStoryHashtag,
  IStoryHashtagParams,
  IStoryList,
  IStoryListParams,
  IUpdateStoryVisibility,
  IUpdateStoryContent,
  IGetStoryTitlesParams,
  IStoryTitleSuggestion,
  IUpdateStoryNameParams,
  IGetStoryVideoDownloadParams,
  IStoryVideoDownloadResponse,
  IGetStoryHashtagsParams,
  IStoryHashtagResponse,
  IGetStoryRequestInfoParams,
  IStoryRequestInfo,
  IGetRecommendedStoriesParams,
  IStoryInSight,
  IGetWhatIfStoryVoice,
} from './type';
import { toast } from 'react-toastify';

export const useCreateStory = (): UseMutationResult<
  IStoryData,
  MutationError,
  ICreateStory
> => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (storyData: ICreateStory) => {
      return createStoryService(storyData);
    },
    onSuccess: () => {
      navigate('/dashboard/library#private-stories');
    },
  });
};

export const useGetStories = (): UseMutationResult<
  IStoryList[],
  MutationError,
  IStoryListParams
> => {
  return useMutation({
    mutationFn: (params: IStoryListParams) => {
      return getStoriesService(params);
    },
  });
};

export const useGetHashtagList = (): UseMutationResult<
  IStoryHashtag[],
  MutationError,
  IStoryHashtagParams
> => {
  return useMutation({
    mutationFn: (params: IStoryHashtagParams) => {
      return getHashtagList(params);
    },
  });
};
export const useGetUserStories = (): UseMutationResult<
  IStoryList[],
  MutationError,
  void
> => {
  return useMutation({
    mutationFn: () => {
      return getUserStoriesService();
    },
  });
};

export const useGetPublicStories = (): UseMutationResult<
  IStoryList[],
  MutationError,
  void
> => {
  return useMutation({
    mutationFn: () => {
      return getPublicStoriesService();
    },
  });
};

export const useGetStoryDetails = (): UseMutationResult<
  IStoryList[],
  MutationError,
  number
> => {
  return useMutation({
    mutationFn: (storyId: number) => {
      return getUserStoryDetailsService(storyId);
    },
  });
};

export const useGetSuggestedStory = (): UseMutationResult<
  IStoryList[],
  MutationError,
  void
> => {
  return useMutation({
    mutationFn: () => {
      return getSuggestedStoryService();
    },
  });
};
export const useGetStoryVoice = (): UseMutationResult<
  string,
  MutationError,
  IGetStoryVoice
> => {
  return useMutation({
    mutationFn: (data: IGetStoryVoice) => {
      return getStoryVoice({
        story_content_id: data.story_id,
        story_id: data.story_content_id,
        story_vorder: data.story_vorder,
        voice_speaker: data.voice_speaker,
        voice_vibe: data.voice_vibe,
      });
    },
  });
};

export const useGetWhatIfStoryVoice = (): UseMutationResult<
  string,
  MutationError,
  IGetWhatIfStoryVoice
> => {
  return useMutation({
    mutationFn: (data: IGetWhatIfStoryVoice) => {
      return getWhatIfStoryVoice({
        story_content_id: data.story_id,
        story_id: data.story_content_id,
        contentVOrder: data.contentVOrder,
      });
    },
  });
};

export const useChangePassword = (): UseMutationResult<
  boolean,
  MutationError,
  IChangePassword
> => {
  return useMutation({
    mutationFn: (formData: IChangePassword) => {
      return changePassword(formData);
    },
  });
};

export const useRateStory = (): UseMutationResult<
  any,
  MutationError,
  IRateData
> => {
  // const navigate = useNavigate();
  return useMutation({
    mutationFn: (storyData: IRateData) => {
      return rateStory(storyData);
    },
    onSuccess: () => {
      toast.success('your feedback submited');
    },
  });
};

export const useSaveStory = (): UseMutationResult<
  string,
  MutationError,
  string
> => {
  return useMutation({
    mutationFn: (storyId: string) => {
      return saveStory(storyId);
    },
    onSuccess: () => {
      toast.success('story saved');
    },
  });
};

export const useUnSaveStory = (): UseMutationResult<
  string,
  MutationError,
  string
> => {
  return useMutation({
    mutationFn: (storyId: string) => {
      return unSaveStory(storyId);
    },
    onSuccess: () => {
      toast.success('story unsaved');
    },
  });
};

export const useUpdateStoryVisibility = (): UseMutationResult<
  boolean,
  MutationError,
  IUpdateStoryVisibility
> => {
  return useMutation({
    mutationFn: (visibilityData: IUpdateStoryVisibility) => {
      return updateStoryVisibility(visibilityData);
    },
    onSuccess: (_, variables) => {
      const message = variables.is_private
        ? 'Your story is now private!'
        : 'Your story is now public!';
      toast.success(message);
    },
  });
};

export const useGetStoryAnalysis = (): UseMutationResult<
  IStoryAnalysis[],
  MutationError,
  number
> => {
  return useMutation({
    mutationFn: (storyId: number) => {
      return getStoryAnalysis(storyId);
    },
  });
};

export const useUpdateStoryContent = (): UseMutationResult<
  boolean,
  MutationError,
  IUpdateStoryContent
> => {
  return useMutation({
    mutationFn: (contentData: IUpdateStoryContent) => {
      return updateStoryContent(contentData);
    },
    onSuccess: () => {
      toast.success('Story content updated successfully!');
    },
  });
};

export const useGetStoryTitles = (): UseMutationResult<
  IStoryTitleSuggestion[],
  MutationError,
  IGetStoryTitlesParams
> => {
  return useMutation({
    mutationFn: (params: IGetStoryTitlesParams) => {
      return getStoryTitles(params);
    },
  });
};

export const useUpdateStoryName = (): UseMutationResult<
  boolean,
  MutationError,
  IUpdateStoryNameParams
> => {
  return useMutation({
    mutationFn: (params: IUpdateStoryNameParams) => {
      return updateStoryName(params);
    },
    onSuccess: () => {
      toast.success('Story title updated successfully!');
    },
  });
};

export const useGetStoryVideoDownload = (): UseMutationResult<
  IStoryVideoDownloadResponse,
  MutationError,
  IGetStoryVideoDownloadParams
> => {
  return useMutation({
    mutationFn: (params: IGetStoryVideoDownloadParams) => {
      return getStoryVideoDownload(params);
    },
  });
};

export const useGetStoryHashtags = (): UseMutationResult<
  IStoryHashtagResponse[],
  MutationError,
  IGetStoryHashtagsParams
> => {
  return useMutation({
    mutationFn: (params: IGetStoryHashtagsParams) => {
      return getStoryHashtags(params);
    },
  });
};

export const useGetStoryRequestInfo = (): UseMutationResult<
  IStoryRequestInfo[],
  MutationError,
  IGetStoryRequestInfoParams
> => {
  return useMutation({
    mutationFn: (params: IGetStoryRequestInfoParams) => {
      return getStoryRequestInfo(params);
    },
  });
};

export const useGetRecommendedStories = (): UseMutationResult<
  IStoryList[],
  MutationError,
  IGetRecommendedStoriesParams
> => {
  return useMutation({
    mutationFn: (params: IGetRecommendedStoriesParams) => {
      return getRecommendedStories(params);
    },
  });
};

export const useStoryInSights = (): UseMutationResult<
  IStoryInSight[],
  MutationError,
  IGetStoryRequestInfoParams
> => {
  return useMutation({
    mutationFn: (params: IGetStoryRequestInfoParams) => {
      return getStoryInSights(params);
    },
  });
};
